import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { ticketAPI, getErrorMessage } from '../../utils/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  MessageCircle,
  BookOpen,
  Video,
  ChevronRight,
  Search,
  HelpCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const SupportNew = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [ticketData, setTicketData] = useState({
    subject: '',
    category: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const faqs = [
    {
      id: 1,
      question: 'How do I buy cryptocurrency?',
      category: 'Getting Started',
      answer: 'To buy cryptocurrency, log in to your account, navigate to the Buy section, select your desired coin, enter the amount, and follow the on-screen instructions to complete your purchase.'
    },
    {
      id: 2,
      question: 'What fees does the platform charge?',
      category: 'Fees & Pricing',
      answer: 'Our platform charges a small trading fee for each transaction. You can view the detailed fee structure on our Fees page.'
    },
    {
      id: 3,
      question: 'How do I secure my account?',
      category: 'Security',
      answer: 'Enable two-factor authentication, use a strong password, and never share your login details with anyone.'
    },
    {
      id: 4,
      question: 'How do I reset my password?',
      category: 'Account Management',
      answer: 'Click on “Forgot Password” on the login page and follow the instructions sent to your registered email address.'
    },
    {
      id: 5,
      question: 'How can I withdraw my funds?',
      category: 'Transactions',
      answer: 'Go to the Withdraw section, select the asset, enter the destination address and amount, then confirm the withdrawal.'
    },
    {
      id: 6,
      question: 'Why is my transaction pending?',
      category: 'Transactions',
      answer: 'Transactions may be pending due to network congestion or additional verification requirements. Please allow some time or contact support if it takes too long.'
    },
    {
      id: 7,
      question: 'How do I enable two-factor authentication?',
      category: 'Security',
      answer: 'Go to your account settings, select Security, and follow the steps to enable two-factor authentication using an authenticator app.'
    },
    {
      id: 8,
      question: 'What is KYC and how do I complete it?',
      category: 'Verification',
      answer: 'KYC (Know Your Customer) is a verification process. Go to the Verification section, upload the required documents, and submit for review.'
    },
    {
      id: 9,
      question: 'How do I contact customer support?',
      category: 'Support',
      answer: 'You can contact customer support via live chat or by submitting a ticket through the Support page.'
    },
    {
      id: 10,
      question: 'How do I change my email address?',
      category: 'Account Management',
      answer: 'Go to your account settings, select Profile, and update your email address. You may be required to verify the new email.'
    },
  ];

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    const payload = {
      subject: ticketData.subject.trim(),
      category: ticketData.category,
      message: ticketData.message.trim(),
    };

    if (!payload.subject || !payload.category || !payload.message) {
      toast.error('Please complete all fields before submitting');
      return;
    }

    try {
      setIsSubmitting(true);
      await ticketAPI.create(payload);
      toast.success('Ticket submitted successfully');
      setTicketData({ subject: '', category: '', message: '' });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to submit ticket'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter FAQs based on search query
  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // State to track which FAQ is open
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div
      className="space-y-6 p-4 md:p-6 lg:pl-10 lg:pr-6 lg:py-10 animate-fadeIn bg-[#F9F9F9]"
      data-testid="support-page"
    >
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          Support
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm md:text-base">
          How can we help you today?
        </p>
      </div>

      {/* Main Search Bar */}
      <div className="flex justify-center items-center mb-6 md:mb-10">
        <div className="relative w-full md:w-[80%] lg:w-[50%]">
          <Input
            type="text"
            placeholder="Search for help..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 py-7 text-base rounded-lg w-full"
          />
          <span className="pointer-events-none absolute left-4 top-1/2 transform -translate-y-1/2">
            <Search className="w-5 h-5 text-gray-400" />
          </span>
        </div>
      </div>
      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-8 space-y-6">
          {/* Support Channels Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer border-none shadow-none">
              <div className="w-12 h-12 rounded-lg bg-[#21bcfe] flex items-center justify-center mb-4">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Live Chat
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                Chat with our support team in real-time
              </p>
              <Button
                className="w-full bg-[#062136] hover:bg-[#062136]/90 text-white font-semibold"
                onClick={() => navigate('/user/messages')}
              >
                Start Chat
              </Button>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer border-none shadow-none">
              <div className="w-12 h-12 rounded-lg bg-[#21bcfe] flex items-center justify-center mb-4">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Help Center
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                Browse our comprehensive knowledge base
              </p>
              <Button
                type="submit"
                className="w-full bg-[#062136] hover:bg-[#062136]/90 text-white font-semibold"
                disabled
              >
                Coming Soon
              </Button>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer border-none shadow-none">
              <div className="w-12 h-12 rounded-lg bg-[#21bcfe] flex items-center justify-center mb-4">
                <Video className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Video Tutorials
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                Watch step-by-step guides and tutorials
              </p>
              <Button
                type="submit"
                className="w-full bg-[#062136] hover:bg-[#062136]/90 text-white font-semibold"
                disabled
              >
                Coming Soon
              </Button>
            </Card>
          </div>

          {/* FAQ Section */}
          <div>
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <HelpCircle className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="space-y-0 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {filteredFaqs.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  No FAQs found for your search.
                </div>
              ) : (
                filteredFaqs.map((faq, index) => (
                  <div key={faq.id}>
                    <button
                      onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                      className={`w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${index !== filteredFaqs.length - 1
                        ? 'border-b border-gray-200 dark:border-gray-700'
                        : ''
                        }`}
                    >
                      <div className="flex-1 text-left">
                        <p className="font-semibold text-gray-900 dark:text-white mb-1">
                          {faq.question}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {faq.category}
                        </p>
                      </div>
                      <ChevronRight className={`w-5 h-5 text-gray-400 flex-shrink-0 ml-4 transition-transform ${openFaq === faq.id ? 'rotate-90' : ''}`} />
                    </button>
                    {openFaq === faq.id && (
                      <div className="p-4 pt-0 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 animate-fadeIn">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Submit Ticket */}
        <div className="lg:col-span-4">
          <Card className="p-6 border-none shadow-none">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Submit a Ticket
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Can't find what you're looking for? Send us a message and we'll
              get back to you within 24 hours.
            </p>
            <form onSubmit={handleSubmitTicket} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Subject
                </label>
                <Input
                  type="text"
                  placeholder="Brief description of your issue"
                  value={ticketData.subject}
                  onChange={(e) =>
                    setTicketData({ ...ticketData, subject: e.target.value })
                  }
                  required
                  className="p-5 text-base rounded-lg bg-gray-100"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Category
                </label>
                <Select
                  value={ticketData.category}
                  onValueChange={(value) =>
                    setTicketData({ ...ticketData, category: value })
                  }
                  className="p-5 text-base rounded-lg bg-gray-100"
                >
                  <SelectTrigger className="p-5 text-base rounded-lg bg-gray-100">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technical">Technical Issue</SelectItem>
                    <SelectItem value="account">Account Problem</SelectItem>
                    <SelectItem value="transaction">
                      Transaction Issue
                    </SelectItem>
                    <SelectItem value="security">Security Concern</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Message
                </label>
                <Textarea
                  placeholder="Describe your issue in detail..."
                  value={ticketData.message}
                  onChange={(e) =>
                    setTicketData({ ...ticketData, message: e.target.value })
                  }
                  className="min-h-[120px] p-5 text-base rounded-lg"
                  style={{ backgroundColor: '#f8f9fa' }}
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#062136] hover:bg-[#062136]/90 text-white font-semibold"
                disabled={
                  isSubmitting ||
                  !ticketData.subject.trim() ||
                  !ticketData.category ||
                  !ticketData.message.trim()
                }
              >
                {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SupportNew;
